import React, { useState, useEffect } from 'react';
import type { PageItem, ReplacePair, ReplaceScope, ReplacePreviewResult } from '../../types';
import { ApiClient } from '../../api/client';
import {
  RefreshCw,
  Search,
  Plus,
  Trash2,
  CheckCircle2,
  AlertTriangle,
  ArrowRight,
  Sparkles,
  CheckSquare,
  Square,
  Zap,
  Calendar,
} from 'lucide-react';

export const TextReplacerView: React.FC = () => {
  const [pages, setPages] = useState<PageItem[]>([]);
  const [selectedPageIds, setSelectedPageIds] = useState<number[]>([]);
  const [pairs, setPairs] = useState<ReplacePair[]>([
    { from: '', to: '' },
  ]);
  const [caseSensitive, setCaseSensitive] = useState(false);
  const [useRegex, setUseRegex] = useState(false);

  // Hatókör és év állapotok
  const [scope, setScope] = useState<ReplaceScope>('all');
  const [targetYear, setTargetYear] = useState<number>(new Date().getFullYear());

  const [loadingPages, setLoadingPages] = useState(true);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [executing, setExecuting] = useState(false);

  // Preview results by page
  const [previewSummary, setPreviewSummary] = useState<{
    totalMatches: number;
    totalAffected: number;
    pageResults: { pageId: number; pageTitle: string; total: number; affected: number; samples: { sample: string; year?: number | null }[] }[];
  } | null>(null);

  const [feedback, setFeedback] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);

  useEffect(() => {
    const load = async () => {
      setLoadingPages(true);
      try {
        const res = await ApiClient.getPages();
        if (res.ok && res.data) {
          setPages(res.data);
          // Default: all pages selected
          setSelectedPageIds(res.data.map(p => p.id));
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoadingPages(false);
      }
    };
    load();
  }, []);

  const handleTogglePage = (pageId: number) => {
    setSelectedPageIds(prev =>
      prev.includes(pageId) ? prev.filter(id => id !== pageId) : [...prev, pageId]
    );
    setPreviewSummary(null);
  };

  const handleSelectAll = (all: boolean) => {
    setSelectedPageIds(all ? pages.map(p => p.id) : []);
    setPreviewSummary(null);
  };

  const handleAddPair = () => {
    setPairs(prev => [...prev, { from: '', to: '' }]);
    setPreviewSummary(null);
  };

  const handleRemovePair = (index: number) => {
    setPairs(prev => prev.filter((_, i) => i !== index));
    setPreviewSummary(null);
  };

  const handlePairChange = (index: number, field: 'from' | 'to', value: string) => {
    setPairs(prev => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
    setPreviewSummary(null);
  };

  const validPairs = pairs.filter(p => p.from.trim().length > 0);

  // Run Preview
  const handlePreview = async () => {
    setFeedback(null);
    if (validPairs.length === 0) {
      setFeedback({ type: 'err', text: 'Adjon meg legalább egy csere párt!' });
      return;
    }
    if (selectedPageIds.length === 0) {
      setFeedback({ type: 'err', text: 'Válasszon ki legalább egy oldalt a cseréhez!' });
      return;
    }

    // Regex check
    if (useRegex) {
      for (const p of validPairs) {
        try {
          new RegExp(p.from);
        } catch (e: any) {
          setFeedback({ type: 'err', text: `Érvénytelen reguláris kifejezés: "${p.from}" (${e.message})` });
          return;
        }
      }
    }

    setPreviewLoading(true);
    let totalMatches = 0;
    let totalAffected = 0;
    const pageResults: { pageId: number; pageTitle: string; total: number; affected: number; samples: { sample: string; year?: number | null }[] }[] = [];

    try {
      for (const pageId of selectedPageIds) {
        const page = pages.find(p => p.id === pageId);
        const res = await ApiClient.replacePageRecords(pageId, {
          pairs: validPairs,
          caseSensitive,
          useRegex,
          preview: true,
          scope,
          targetYear: scope !== 'all' ? targetYear : undefined,
        });
        if (res.ok) {
          const payload = (res.data || (res as any)) as ReplacePreviewResult;
          const t = payload?.total || 0;
          const a = payload?.affected || 0;
          if (t > 0) {
            totalMatches += t;
            totalAffected += a;
            pageResults.push({
              pageId,
              pageTitle: page?.title || `Oldal #${pageId}`,
              total: t,
              affected: a,
              samples: (payload?.preview || []).map((s: any) => ({ sample: s.sample, year: s.year })),
            });
          }
        }
      }

      setPreviewSummary({
        totalMatches,
        totalAffected,
        pageResults,
      });

      if (totalMatches === 0) {
        setFeedback({ type: 'ok', text: 'Nem található egyezés a kijelölt oldalakon a megadott hatókörben.' });
      }
    } catch (err: any) {
      setFeedback({ type: 'err', text: 'Hiba történt az előnézet számításakor: ' + err.message });
    } finally {
      setPreviewLoading(false);
    }
  };

  // Run Execution
  const handleExecute = async () => {
    if (!previewSummary || previewSummary.totalMatches === 0) return;
    const scopeLabel = scope === 'all'
      ? 'Minden évben (globális)'
      : (scope === 'exact_year' ? `Csak ${targetYear}-ban` : `${targetYear}-tól kezdve`);

    if (
      !confirm(
        `Biztosan végrehajtja a cserét ${previewSummary.pageResults.length} oldalon?\n` +
        `Hatókör: ${scopeLabel}\n` +
        `Ez ${previewSummary.totalAffected} rekordot és ${previewSummary.totalMatches} találatot fog azonnal módosítani!`
      )
    ) {
      return;
    }

    setExecuting(true);
    setFeedback(null);
    let totalDone = 0;

    try {
      for (const r of previewSummary.pageResults) {
        const res = await ApiClient.replacePageRecords(r.pageId, {
          pairs: validPairs,
          caseSensitive,
          useRegex,
          preview: false,
          scope,
          targetYear: scope !== 'all' ? targetYear : undefined,
        });
        if (res.ok) {
          const payload = (res.data || (res as any)) as ReplacePreviewResult;
          totalDone += payload?.affected || 0;
        }
      }

      setFeedback({
        type: 'ok',
        text: `✅ Sikeres csere! ${totalDone} rekord módosítva a kijelölt oldalakon.`,
      });
      setPreviewSummary(null);
    } catch (err: any) {
      setFeedback({ type: 'err', text: 'Hiba a végrehajtás során: ' + err.message });
    } finally {
      setExecuting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Címsor */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-slate-200 dark:border-slate-800">
        <div>
          <div className="flex items-center gap-2 text-xs font-semibold text-blue-600 dark:text-blue-400 uppercase tracking-wider">
            <span>Keresés & Csere</span>
          </div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2 mt-0.5">
            <RefreshCw className="w-6 h-6 text-blue-600" />
            <span>Tömeges Szövegcsere</span>
          </h1>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
            Keresés és csere a szervezet közzétételi oldalainak rekordjaiban (pl. névváltozás, címcsere, tisztségviselő csere).
          </p>
        </div>
      </div>

      {feedback && (
        <div
          className={`p-4 rounded-xl text-xs flex items-center gap-2.5 animate-in fade-in ${
            feedback.type === 'ok'
              ? 'bg-emerald-50 dark:bg-emerald-950/40 text-emerald-800 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800'
              : 'bg-red-50 dark:bg-red-950/40 text-red-800 dark:text-red-300 border border-red-200 dark:border-red-800'
          }`}
        >
          {feedback.type === 'ok' ? (
            <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
          ) : (
            <AlertTriangle className="w-4 h-4 text-red-600 shrink-0" />
          )}
          <span>{feedback.text}</span>
        </div>
      )}

      {/* Fő Kétoszlopos Panel */}
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-xs overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between bg-slate-50/50 dark:bg-slate-950/30">
          <span className="font-bold text-sm text-slate-900 dark:text-white flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-blue-600" />
            <span>Szöveg csere – kijelölt oldalakban</span>
          </span>
          <span className="text-[11px] text-slate-400">Csak a saját szervezet rekordjain</span>
        </div>

        <div className="p-6 grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* Bal oszlop: Beállítások & Csere párok */}
          <div className="lg:col-span-6 space-y-6">
            {/* 1. Oldalak kiválasztása */}
            <div className="space-y-2.5">
              <label className="text-xs font-bold text-slate-800 dark:text-slate-200 uppercase tracking-wider block">
                Oldalak kiválasztása
              </label>

              {loadingPages ? (
                <div className="py-4 text-center text-slate-400 text-xs flex items-center justify-center gap-2">
                  <RefreshCw className="w-4 h-4 animate-spin text-blue-500" />
                  <span>Oldalak betöltése...</span>
                </div>
              ) : (
                <div className="space-y-1.5 max-h-48 overflow-y-auto pr-1">
                  {pages.map(p => {
                    const isSelected = selectedPageIds.includes(p.id);
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => handleTogglePage(p.id)}
                        className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-left text-xs transition border cursor-pointer ${
                          isSelected
                            ? 'bg-blue-50/70 dark:bg-blue-950/40 text-blue-900 dark:text-blue-200 border-blue-200 dark:border-blue-800/80 font-medium'
                            : 'bg-slate-50/50 dark:bg-slate-950/20 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-800 hover:bg-slate-100 dark:hover:bg-slate-800'
                        }`}
                      >
                        {isSelected ? (
                          <CheckSquare className="w-4 h-4 text-blue-600 dark:text-blue-400 shrink-0" />
                        ) : (
                          <Square className="w-4 h-4 text-slate-400 shrink-0" />
                        )}
                        <span className="truncate">{p.title}</span>
                      </button>
                    );
                  })}
                </div>
              )}

              <div className="flex gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => handleSelectAll(true)}
                  className="px-2.5 py-1 text-xs bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-lg cursor-pointer transition font-medium"
                >
                  Mind
                </button>
                <button
                  type="button"
                  onClick={() => handleSelectAll(false)}
                  className="px-2.5 py-1 text-xs bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-lg cursor-pointer transition font-medium"
                >
                  Egyik sem
                </button>
              </div>
            </div>

            {/* 2. Csere párok */}
            <div className="space-y-2.5 pt-2 border-t border-slate-100 dark:border-slate-800">
              <label className="text-xs font-bold text-slate-800 dark:text-slate-200 uppercase tracking-wider block">
                Csere párok
              </label>

              <div className="space-y-2">
                {pairs.map((pair, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <input
                      type="text"
                      placeholder="Keresett szöveg"
                      value={pair.from}
                      onChange={(e) => handlePairChange(idx, 'from', e.target.value)}
                      className="flex-1 px-3 py-2 bg-slate-50 dark:bg-slate-950/50 border border-slate-200 dark:border-slate-700 rounded-xl text-xs text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono"
                    />
                    <ArrowRight className="w-4 h-4 text-slate-400 shrink-0" />
                    <input
                      type="text"
                      placeholder="Csere szöveg"
                      value={pair.to}
                      onChange={(e) => handlePairChange(idx, 'to', e.target.value)}
                      className="flex-1 px-3 py-2 bg-slate-50 dark:bg-slate-950/50 border border-slate-200 dark:border-slate-700 rounded-xl text-xs text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono"
                    />
                    {pairs.length > 1 && (
                      <button
                        type="button"
                        onClick={() => handleRemovePair(idx)}
                        className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/40 rounded-xl transition cursor-pointer"
                        title="Sor törlése"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>

              <button
                type="button"
                onClick={handleAddPair}
                className="px-3 py-1.5 text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 bg-blue-50 hover:bg-blue-100 dark:bg-blue-950/40 rounded-xl font-semibold flex items-center gap-1.5 transition cursor-pointer"
              >
                <Plus className="w-3.5 h-3.5" />
                <span>Sor hozzáadása</span>
              </button>

              {/* Hatókör választó: Globális elírás vs. Időszaki/Apparátusváltás */}
              <div className="space-y-2 pt-2 border-t border-slate-100 dark:border-slate-800">
                <label className="text-xs font-bold text-slate-800 dark:text-slate-200 uppercase tracking-wider block">
                  Csere Hatóköre (Év / Elírás)
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => { setScope('all'); setPreviewSummary(null); }}
                    className={`p-3 rounded-xl border text-left transition cursor-pointer flex flex-col gap-1 ${
                      scope === 'all'
                        ? 'bg-blue-50/80 dark:bg-blue-950/40 border-blue-300 dark:border-blue-700 text-blue-950 dark:text-blue-200'
                        : 'bg-slate-50/50 dark:bg-slate-900/30 border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-xs flex items-center gap-1.5">
                        <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                        Globális elírás javítás
                      </span>
                      {scope === 'all' && <CheckCircle2 className="w-4 h-4 text-blue-600 dark:text-blue-400" />}
                    </div>
                    <span className="text-[11px] opacity-80 leading-snug">
                      Minden évben és általános rekordban javít (pl. Tót Áron ➔ Tóth Áron elírás).
                    </span>
                  </button>

                  <button
                    type="button"
                    onClick={() => { setScope('exact_year'); setPreviewSummary(null); }}
                    className={`p-3 rounded-xl border text-left transition cursor-pointer flex flex-col gap-1 ${
                      scope !== 'all'
                        ? 'bg-emerald-50/80 dark:bg-emerald-950/40 border-emerald-300 dark:border-emerald-700 text-emerald-950 dark:text-emerald-200'
                        : 'bg-slate-50/50 dark:bg-slate-900/30 border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-xs flex items-center gap-1.5">
                        <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                        Időszaki / Apparátusváltás
                      </span>
                      {scope !== 'all' && <CheckCircle2 className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />}
                    </div>
                    <span className="text-[11px] opacity-80 leading-snug">
                      Csak a megadott évben cserél (pl. Kovács R. ➔ Balogh C., archív évek maradnak).
                    </span>
                  </button>
                </div>

                {scope !== 'all' && (
                  <div className="p-3 bg-emerald-50/60 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800/60 rounded-xl flex flex-wrap items-center gap-3 text-xs text-emerald-900 dark:text-emerald-200">
                    <span className="font-semibold flex items-center gap-1">
                      <Calendar className="w-3.5 h-3.5 text-emerald-600" />
                      Cél év:
                    </span>
                    <input
                      type="number"
                      min={2000}
                      max={2099}
                      value={targetYear}
                      onChange={(e) => { setTargetYear(parseInt(e.target.value, 10) || new Date().getFullYear()); setPreviewSummary(null); }}
                      className="w-20 px-2.5 py-1 bg-white dark:bg-slate-900 border border-emerald-300 dark:border-emerald-700 rounded-lg text-xs font-bold text-center"
                    />
                    <select
                      value={scope}
                      onChange={(e) => { setScope(e.target.value as ReplaceScope); setPreviewSummary(null); }}
                      className="px-2.5 py-1 bg-white dark:bg-slate-900 border border-emerald-300 dark:border-emerald-700 rounded-lg text-xs font-medium cursor-pointer"
                    >
                      <option value="exact_year">Csak ebben az évben ({targetYear})</option>
                      <option value="future_years">Ettől az évtől kezdve ({targetYear} és újabbak)</option>
                    </select>
                    <span className="text-[11px] text-emerald-700 dark:text-emerald-300 italic">
                      A korábbi évek archív adatai érintetlenek maradnak!
                    </span>
                  </div>
                )}
              </div>

              {/* Opciók */}
              <div className="flex flex-wrap items-center gap-5 pt-2">
                <label className="flex items-center gap-2 text-xs text-slate-700 dark:text-slate-300 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={caseSensitive}
                    onChange={(e) => { setCaseSensitive(e.target.checked); setPreviewSummary(null); }}
                    className="w-4 h-4 rounded text-blue-600 focus:ring-blue-500 cursor-pointer"
                  />
                  <span>Kis/nagybetű érzékeny</span>
                </label>
                <label className="flex items-center gap-2 text-xs text-slate-700 dark:text-slate-300 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={useRegex}
                    onChange={(e) => { setUseRegex(e.target.checked); setPreviewSummary(null); }}
                    className="w-4 h-4 rounded text-blue-600 focus:ring-blue-500 cursor-pointer"
                  />
                  <span>Reguláris kifejezés</span>
                </label>
              </div>

              {/* Műveleti gombok */}
              <div className="flex flex-wrap gap-2.5 pt-3">
                <button
                  type="button"
                  onClick={handlePreview}
                  disabled={previewLoading || validPairs.length === 0 || selectedPageIds.length === 0}
                  className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-xl text-xs font-semibold flex items-center gap-2 shadow-xs transition cursor-pointer"
                >
                  {previewLoading ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Search className="w-4 h-4" />
                  )}
                  <span>Előnézet</span>
                </button>

                {previewSummary && previewSummary.totalMatches > 0 && (
                  <button
                    type="button"
                    onClick={handleExecute}
                    disabled={executing}
                    className="px-5 py-2.5 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white rounded-xl text-xs font-semibold flex items-center gap-2 shadow-xs transition cursor-pointer"
                  >
                    {executing ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <Zap className="w-4 h-4" />
                    )}
                    <span>Csere végrehajtása</span>
                  </button>
                )}

                {previewSummary && (
                  <button
                    type="button"
                    onClick={() => setPreviewSummary(null)}
                    className="px-4 py-2.5 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300 rounded-xl text-xs font-medium cursor-pointer transition"
                  >
                    Mégse
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Jobb oszlop: Előnézet / Eredmény */}
          <div className="lg:col-span-6 space-y-3">
            <label className="text-xs font-bold text-slate-800 dark:text-slate-200 uppercase tracking-wider block">
              Előnézet / Eredmény
            </label>

            <div className="bg-slate-50 dark:bg-slate-950/40 border border-slate-200 dark:border-slate-800 rounded-2xl p-5 min-h-[260px] text-xs">
              {!previewSummary ? (
                <div className="text-slate-400 py-12 text-center space-y-2">
                  <Search className="w-8 h-8 mx-auto text-slate-300 dark:text-slate-700" />
                  <p>Adja meg a csere párokat és kattintson az Előnézet gombra.</p>
                </div>
              ) : previewSummary.totalMatches === 0 ? (
                <div className="text-emerald-700 dark:text-emerald-400 py-12 text-center space-y-2">
                  <CheckCircle2 className="w-8 h-8 mx-auto text-emerald-500" />
                  <p className="font-semibold">Nem található egyezés a kijelölt oldalakon.</p>
                  <p className="text-[11px] text-slate-400">A rekordok nem tartalmazzák a megadott keresett kifejezést a kiválasztott hatókörben.</p>
                </div>
              ) : (
                <div className="space-y-4 animate-in fade-in">
                  {/* Összegző banner */}
                  <div className="p-3.5 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 rounded-xl text-amber-900 dark:text-amber-200 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />
                    <div>
                      <strong className="font-bold">{previewSummary.totalMatches} egyezés</strong> – {previewSummary.totalAffected} rekord fog módosulni {previewSummary.pageResults.length} oldalon.
                    </div>
                  </div>

                  {/* Csere párok összefoglalása */}
                  <div className="space-y-1 text-slate-600 dark:text-slate-400">
                    <span className="font-semibold text-[11px] text-slate-700 dark:text-slate-300">Csere párok:</span>
                    <div className="flex flex-wrap gap-1.5 pt-1">
                      {validPairs.map((p, i) => (
                        <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-slate-200 dark:bg-slate-800 font-mono text-[11px]">
                          <span className="text-red-600 dark:text-red-400">"{p.from}"</span>
                          <ArrowRight className="w-3 h-3 text-slate-400" />
                          <span className="text-emerald-600 dark:text-emerald-400">"{p.to}"</span>
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Oldalankénti bontás */}
                  <div className="space-y-2.5 max-h-60 overflow-y-auto pr-1">
                    {previewSummary.pageResults.map(pr => (
                      <div key={pr.pageId} className="p-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl space-y-1.5">
                        <div className="flex items-center justify-between font-bold text-slate-800 dark:text-slate-200">
                          <span className="truncate">{pr.pageTitle}</span>
                          <span className="px-2 py-0.5 rounded-full bg-blue-100 dark:bg-blue-950/60 text-blue-700 dark:text-blue-300 text-[10px] font-mono shrink-0">
                            {pr.affected} rekord · {pr.total} találat
                          </span>
                        </div>
                        {pr.samples.length > 0 && (
                          <div className="space-y-1">
                            {pr.samples.map((s, si) => (
                              <div key={si} className="bg-slate-50 dark:bg-slate-950 p-2 rounded-lg font-mono text-[10px] text-slate-600 dark:text-slate-400 flex items-center gap-2">
                                <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold shrink-0 ${
                                  s.year
                                    ? 'bg-blue-100 dark:bg-blue-950 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800'
                                    : 'bg-slate-200 dark:bg-slate-800 text-slate-600 dark:text-slate-400'
                                }`}>
                                  {s.year ? `${s.year}. év` : 'Általános'}
                                </span>
                                <span className="truncate">{s.sample}...</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  <div className="text-[11px] text-slate-400 pt-2 border-t border-slate-200 dark:border-slate-800">
                    ⚠️ A végrehajtás azonnal módosítja az adatbázist. Nagyobb csere előtt célszerű biztonsági mentést (XLSX export) készíteni!
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
